// Document Editor Modal for IDA
// Provides a full-screen modal with MMEditor for editing txt, md, and docx files

import React, { useState, useEffect, useMemo } from "react";
import { MMEditor, createIconPackPlugin } from "@mmeditor/core";
import "@mmeditor/core/style.css";
import { marked } from "marked";
import TurndownService from "turndown";
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  Modal,
  ModalDialog,
  Typography,
} from "@mui/joy";

// MUI icons for MMEditor toolbar
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";

import type { IDADocument } from "../common/types";

// Create MUI icon pack configuration for MMEditor
const muiIconConfig = {
  icons: {
    bold: <FormatBoldIcon fontSize="small" />,
    italic: <FormatItalicIcon fontSize="small" />,
    underline: <FormatUnderlinedIcon fontSize="small" />,
    strike: <StrikethroughSIcon fontSize="small" />,
    code: <CodeIcon fontSize="small" />,
    "code-block": <DataObjectIcon fontSize="small" />,
    bullet: <FormatListBulletedIcon fontSize="small" />,
    number: <FormatListNumberedIcon fontSize="small" />,
    link: <LinkIcon fontSize="small" />,
    table: <TableChartIcon fontSize="small" />,
    clear: <FormatClearIcon fontSize="small" />,
  },
};

const iconPackPlugin = createIconPackPlugin(muiIconConfig);

// Formats that use the rich WYSIWYG editor (markdown round-trip)
const RICH_EDITOR_FORMATS = new Set(["md", "docx", "doc"]);

/**
 * Determine the file extension from a filename.
 */
function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

interface DocumentEditorModalProps {
  open: boolean;
  document: IDADocument | null;
  initialContent: string;
  originalFormat?: string;
  onClose: () => void;
  onSave: (content: string) => Promise<void>;
  saving: boolean;
}

export const DocumentEditorModal: React.FC<DocumentEditorModalProps> = ({
  open,
  document,
  initialContent,
  originalFormat,
  onClose,
  onSave,
  saving,
}) => {
  // HTML content for the rich editor (MMEditor works with HTML)
  const [htmlContent, setHtmlContent] = useState("");
  const [initialHtmlContent, setInitialHtmlContent] = useState("");
  // Plain text content for passthrough formats
  const [plainContent, setPlainContent] = useState("");
  const [initialPlainContent, setInitialPlainContent] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Determine if this document uses a plain text editor (anything not md/docx)
  const isPlainText = useMemo(() => {
    if (!document) return false;
    const ext = getFileExtension(document.filename);
    return ext !== "" && !RICH_EDITOR_FORMATS.has(ext);
  }, [document]);

  // Initialize TurndownService for HTML->Markdown conversion
  const turndownService = useMemo(() => {
    const service = new TurndownService({
      headingStyle: "atx",
      codeBlockStyle: "fenced",
      bulletListMarker: "-",
      strongDelimiter: "**",
      emDelimiter: "_",
    });

    // Add table support
    service.addRule("tableCell", {
      filter: ["th", "td"],
      replacement: function (content: string, node: Node) {
        const tableCell = node as HTMLTableCellElement;
        const index = tableCell.cellIndex;
        const prefix = index === 0 ? "| " : "";
        return prefix + content + " |";
      },
    });

    service.addRule("table", {
      filter: ["table"],
      replacement: function (_content: string, node: Node) {
        const table = node as HTMLTableElement;
        const rows = table.rows;
        if (rows.length === 0) return "";
        const headers = Array.from(rows[0].cells) as HTMLTableCellElement[];
        const headerRow = headers
          .map((header) => header.textContent?.trim() || "")
          .join(" | ");
        const separator = headers.map(() => "---").join(" | ");
        const dataRows = Array.from(rows)
          .slice(1)
          .map((row) =>
            Array.from(row.cells as HTMLCollectionOf<HTMLTableCellElement>)
              .map((cell) => cell.textContent?.trim() || "")
              .join(" | "),
          );
        return (
          "\n\n| " +
          headerRow +
          " |\n| " +
          separator +
          " |\n| " +
          dataRows.join(" |\n| ") +
          " |\n\n"
        );
      },
    });

    return service;
  }, []);

  // Initialize content when modal opens
  useEffect(() => {
    if (initialContent) {
      if (isPlainText) {
        // Plain text formats: load raw content directly
        setPlainContent(initialContent);
        setInitialPlainContent(initialContent);
      } else {
        // Markdown/docx: convert to HTML for WYSIWYG editor
        const html = marked.parse(initialContent, { async: false }) as string;
        setHtmlContent(html);
        setInitialHtmlContent(html);
      }
      setHasChanges(false);
    }
  }, [initialContent, document?.id, isPlainText]);

  const handleContentChange = (newHtml: string) => {
    setHtmlContent(newHtml);
    setHasChanges(newHtml !== initialHtmlContent);
  };

  const handlePlainContentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setPlainContent(e.target.value);
    setHasChanges(e.target.value !== initialPlainContent);
  };

  const handleSave = async () => {
    if (isPlainText) {
      // Save raw content as-is for passthrough formats
      await onSave(plainContent);
    } else {
      // Convert HTML back to markdown before saving
      const markdown = turndownService.turndown(htmlContent);
      await onSave(markdown);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!document) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: "min(800px, 90vw)",
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <DialogTitle sx={{ p: 0, mb: 0.5 }}>
              {document.filename}
            </DialogTitle>
            {originalFormat === "docx" && (
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Editing as Markdown (will be converted back to Word on save)
              </Typography>
            )}
            {isPlainText && (
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Editing as plain text (
                {getFileExtension(document.filename).toUpperCase()})
              </Typography>
            )}
          </Box>
          {hasChanges && (
            <Typography level="body-sm" sx={{ color: "warning.500" }}>
              Unsaved changes
            </Typography>
          )}
        </Box>

        {/* Editor */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "& [contenteditable]": {
              wordWrap: "break-word",
              overflowWrap: "break-word",
              overflowX: "hidden",
            },
          }}
        >
          {isPlainText ? (
            <textarea
              value={plainContent}
              onChange={handlePlainContentChange}
              placeholder="Start typing..."
              spellCheck={false}
              style={{
                width: "100%",
                height: "100%",
                flex: 1,
                resize: "none",
                border: "none",
                outline: "none",
                padding: "16px",
                fontFamily: "monospace",
                fontSize: "14px",
                lineHeight: "1.5",
                backgroundColor: "transparent",
                color: "inherit",
                boxSizing: "border-box",
              }}
            />
          ) : (
            <MMEditor
              value={htmlContent}
              onChange={handleContentChange}
              plugins={[iconPackPlugin]}
              style={{
                height: "100%",
                width: "100%",
                flex: 1,
                overflow: "auto",
              }}
              placeholder="Start typing..."
            />
          )}
        </Box>

        {/* Footer with actions */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            startDecorator={saving ? <CircularProgress size="sm" /> : null}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default DocumentEditorModal;
